import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';

import { useUserStore } from './user';
import { useAccountsStore } from './accounts';

import dayjs, { Dayjs } from 'dayjs';

import { getSensors, getSensorRangeOptions } from '@/utils/api';

import type { SensorType, SensorIndexQuery, SensorDistanceSettingType } from '@/types/Sensor';
import { getParamsOnLoad } from '@/utils/storeMixin';

export const useSensorsStore = defineStore('sensors', () => {
  const userStore = useUserStore();
  const accountsStore = useAccountsStore();
  const route = useRoute();

  const sensorsData = ref<SensorType[]>([]);
  const lastPage = ref(0);
  const lastUpdated = ref<Dayjs>();
  const loading = ref(false);

  const sensorTypes = ['TP-Link TL-MR3020 v3'];

  const unassigningSensor = ref<SensorType | null>();

  const sensorDistanceOptions = ref<SensorDistanceSettingType[]>([]);

  const params = ref<SensorIndexQuery>({
    page: 1,
    results_per_page: 10,
    search: '',
    sortDesc: false,
    sort: 'name',
  });

  function getQueryParamsOnLoad() {
    if (route.path.includes('/sensors')) {
      params.value = getParamsOnLoad(params.value);
      getData();
    }
  }

  const getData = async (override = false, ignoreLoad = false) => {
    // Skip the load, used for when we simply update the list behind the scenes
    if (!ignoreLoad) {
      loading.value = true;
    }

    // Use the Store Cache if we have it.
    if (override === false && sensorsData.value.length) {
      loading.value = false;
      return sensorsData.value;
    }

    const newParams = { ...params.value };

    newParams.sortDesc = newParams.sortDesc === true ? 1 : 0;
    newParams.include_inactive = newParams.include_inactive === true ? 1 : 0;

    if (newParams.account === null) {
      delete newParams.account;
    }

    await accountsStore.getAccountDataSimple();

    getSensors(userStore.accessToken, newParams).then((response) => {
      const { data, last_page } = response;

      if (data.length) {
        data.map((sensor: SensorType) => {
          sensor.account =
            accountsStore.accountsDataSimple.find((acct) => acct.id === sensor.account_id)
              ?.display_name || '';
          return sensor;
        });

        getSensorRangeOptions(userStore.accessToken, userStore.isStaff, data[0].id).then((resp) => {
          sensorDistanceOptions.value = resp;
        });
      }

      sensorsData.value = data;
      lastPage.value = last_page;
      lastUpdated.value = dayjs();
      loading.value = false;
    });
  };

  getQueryParamsOnLoad();

  return {
    sensorsData,
    lastPage,
    params,
    loading,
    sensorTypes,
    unassigningSensor,
    sensorDistanceOptions,

    getData,
  };
});
